<template>
  <div class="research-achievements">
    <!-- 页面标题 -->
    <achiTitle title="科研成果展示平台" subtitle="探索最新科研成果，促进技术转化与交流" />
    <!-- 筛选区域 -->
    <div class="filter-bar">
      <el-input v-model="searchQuery" 
      placeholder="搜索成果名称..." prefix-icon="Search" clearable
        class="search-input"></el-input>
      <el-button size="large" @click="searchByName">搜索</el-button>

      <el-select 
        v-model="selectedUnit" 
        placeholder="选择所属领域" 
        clearable 
        class="filter-select"
        @change="handleSelectChange">
        <el-option v-for="unit in category" :key="unit" :label="unit"
          :value="unit"></el-option>
      </el-select>
    </div>
    <!-- 成果展示区域 -->
    <div class="achievements-container">
      <div v-if="cardDatas.length === 0 && !loading" class="no-results">
        <el-empty description="没有找到符合条件的科研成果"></el-empty>
      </div>
      <!-- 加载中状态 -->
      <div v-if="loading" class="loading-container">
        <el-loading type="circle" text="加载中..."></el-loading>
      </div>

      <div class="achievement-card" v-for="(achievement, index) in cardDatas" :key="index">
        <div class="card-header">
          <h3 class="achievement-title">{{ achievement.projectName }}</h3>
          <el-tag :type="getStatusTagType(achievement.projectProgress)" class="status-tag">
            {{ achievement.projectProgress }}
          </el-tag>
        </div>

        <div class="card-body" @click="goToDetail(achievement)">
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">所属机构:</span>
              <span class="info-value">{{ achievement.institution?.name || '未知机构' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">技术领域：</span>
              <span class="info-value">{{ achievement.category || '未知领域' }}</span>
            </div>
            <div class="info-item price-item">
              <span class="info-label">拟定转化金额：</span>
              <span class="info-value">{{ achievement.proposedTransformationAmount || '0' }}</span>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <el-button type="primary" size="small" @click="goToDetail(achievement)">查看详情</el-button>
        </div>
      </div>
    </div>

    <div class="pagination">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 30]"
        :page-size="pagination.pageSize"
        layout="total, prev, pager, next,sizes"
        :total="total"></el-pagination>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import achiTitle from '../components/achievement/achi_title.vue';
import { loadPageData } from '@/utils/MyUtils.js'
import achievementApi from '@/api/achievements.js'

export default {
  name: 'ResearchAchievements',
  components: {
    achiTitle
  },
  data() {
    return {
      // 筛选表单数据（页面切换时保留）
      searchQuery: '',
      selectedUnit: '',
      // 分页配置（合并为对象，统一管理）
      pagination: {
        currentPage: 1,
        pageSize: 12,
        total: 0
      },
      // 数据相关
      cardDatas: [],
      loading: true,
      error: null
    };
  },
  computed: {
    // 核心：动态拼接组合查询URL（分页+搜索+筛选）
    baseUrl() {
      const { currentPage, pageSize } = this.pagination;
      let url = `/achievements?pageNum=${currentPage}&pageSize=${pageSize}`;
      
      // 拼接搜索参数（成果名称模糊查询）
      if (this.searchQuery.trim()) {
        url += `&projectName=${encodeURIComponent(this.searchQuery.trim())}`;
      }
      // 拼接筛选参数（所属领域精确查询）
      if (this.selectedUnit) {
        url += `&category=${encodeURIComponent(this.selectedUnit)}`;
      }
      return url;
    },
    // 技术领域选项（从接口获取）
    category() {
      return achievementApi.category || [];
    }
  },
  watch: {
    // 监听搜索关键词变化：重置页码+加载数据
    searchQuery: {
      handler() {
        this.pagination.currentPage = 1;
        this.loadData(this.baseUrl);
      },
      immediate: false
    },
    // 监听筛选条件变化：重置页码+加载数据
    selectedUnit: {
      handler() {
        this.pagination.currentPage = 1;
        this.loadData(this.baseUrl);
      },
      immediate: false
    },
    // 监听路由变化：页面切换后重新加载数据（保留筛选状态）
    $route: {
      handler() {
        this.loadData(this.baseUrl);
      },
      immediate: false
    }
  },
  created() {
    // 组件创建时加载初始数据（携带当前筛选条件）
    this.loadData(this.baseUrl);
  },
  methods: {
    // 从接口加载数据（统一处理组合查询+分页）
    async loadData(url) {
      try {
        this.loading = true;
        let data = await loadPageData(this, url);
        this.total = data.total || 0;
        this.cardDatas = data.records || [];
        this.error = null;
      } catch (err) {
        this.error = '数据加载失败，请刷新页面重试';
        this.cardDatas = []; 
        console.error('加载数据出错:', err);
      } finally {
        this.loading = false;
      }
    },
    // 项目状态标签类型映射（优化：兼容null/undefined）
    getStatusTagType(progress) {
      const status = String(progress || '').trim();
      const statusMap = {
        '洽谈中': 'info',
        '已立项': 'primary',
        '开发中': 'warning',
        '已应用': 'success',
        '临床前': 'warning'
      };
      return statusMap[status] || 'info';
    },
    // 跳转到成果详情界面
    goToDetail(achievement) {
      this.$router.push({
        name: 'AchievementDetail',
        params: { id: achievement.id }
      });
    },
    // 每页数量变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 当前页码变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadData(this.baseUrl);
    },
    // 搜索按钮点击（与watch逻辑一致，避免重复代码）
    searchByName() {
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 领域筛选（简化：直接赋值，watch自动触发加载）
    searchByPlatform(pName) {
      this.selectedUnit = pName;
    },
    // 筛选下拉框变化（直接赋值，watch自动处理）
    handleSelectChange(value) {
      this.selectedUnit = value;
    }
  }
};
</script>

<style scoped>
.research-achievements {
  max-width: 85%;
  margin: 0 auto;
  padding: 20px;
}

/* 筛选区域：靠左排列，优化布局 */
.filter-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
}

.filter-select {
  width: 200px;
}

.search-input {
  width: 300px;
  min-width: 180px;
}

/* 成果展示区域：网格布局（保持原有卡片样式，优化间距） */
.achievements-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
  position: relative;
}

/* 加载中样式 */
.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.achievement-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
}

.achievement-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.achievement-title {
  font-size: 18px;
  font-weight: 500;
  color: #1f2d3d;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  max-width: 75%;
}

.status-tag {
  width: 60px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
}

.card-body {
  padding: 20px;
  cursor: pointer;
}

/* 优化卡片内容布局：横向排列更紧凑 */
.info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.info-item {
  font-size: 14px;
  line-height: 1.5;
  width: calc(50% - 8px); /* 两列布局，适配卡片宽度 */
}

.info-label {
  color: #86909c;
  display: inline-block;
  width: 100px;
  font-weight: 500;
}

.info-value {
  color: #1f2d3d;
  vertical-align: left;
}

.price-item .info-value {
  color: #f56c6c;
  font-weight: 500;
}

.card-footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  text-align: right;
  background-color: #fafafa;
}

.pagination {
  text-align: center;
  margin-top: 30px;
}

.no-results {
  text-align: center;
  padding: 60px 0;
  grid-column: 1 / -1; /* 占满所有列，居中显示 */
}

/* 响应式调整 */
@media (max-width: 768px) {
  .research-achievements {
    max-width: 95%;
    padding: 15px;
  }
  
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-select, .search-input {
    width: 100%;
  }
  
  .achievements-container {
    grid-template-columns: 1fr; /* 小屏幕单行显示 */
  }
  
  .info-item {
    width: 100%; /* 小屏幕单列布局 */
  }
  
  .achievement-title {
    max-width: 60%;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .info-item {
    width: 100%; /* 中等屏幕单列布局，避免文字挤压 */
  }
}
</style>